
import { defineComponent, ref, reactive, computed, watch, nextTick, onMounted } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { OioPagination, OioForm, OioFormItem, OioInput, OioButton, OioTag, OioSpin } from '@kunlun/vue-ui-antd';
import {
  BillingBill,
  BillingBillLine,
  BillingLineStatus,
  BillingType,
  DeliInvoiceInfo,
  IndependentBillingCandidate,
  IndependentBillingCandidateFilter,
  IndependentBillingCreateParam,
  IndependentBillingDistributor,
  IndependentBillingSalesOrg,
  SpecifiedInvoiceContentItem
} from '../types';
import {
  queryDistributorOptions,
  querySalesOrgOptions,
  queryInvoiceInfoOptions,
  queryDefaultInvoiceInfo,
  queryCandidateOrders,
  createIndependentBilling
} from '../api/billingBill';
import { useBillingStatus } from '../hooks/useBillingStatus';
import { useBillingAmount } from '../hooks/useBillingAmount';
import {
  billingLineStatusTextMap,
  billingTypeTextMap,
  sapPackStatusTextMap,
  sapPackStatusColorMap,
  outStockStatusTextMap,
  sourceDocTypeTextMap
} from '../constants';
import SpecifyInvoiceDialog from './SpecifyInvoiceDialog.vue';

/** 档案默认开票信息在下拉中的占位值：后端返回的档案组装对象不带 id，提交时 invoiceInfoId 传空 */
const DEFAULT_INVOICE_INFO = 'default';

/** 经销商下拉检索防抖间隔 */
const DISTRIBUTOR_SEARCH_DEBOUNCE = 300;

/**
 * 独立开票创建向导（两步）。
 * 第一步确定开票上下文（经销商 → 销售机构 → 开票信息 → 开票类型），第二步勾选候选 80/90 单并发起打包。
 * 提交时后端会按候选查询同一套准入规则重新校验所选单据，前端勾选结果不作为唯一依据。
 */
export default defineComponent({
  name: 'IndependentBillingCreatePage',
  components: {
    SpecifyInvoiceDialog,
    OioPagination,
    OioForm,
    OioFormItem,
    OioInput,
    OioButton,
    OioTag,
    OioSpin
  },
  emits: ['success', 'cancel'],
  setup(props, { emit }) {
    const { getLineStatusText, getLineStatusColor } = useBillingStatus();
    const { formatAmount, getAmountClass } = useBillingAmount();

    const currentStep = ref(0);
    const submitting = ref(false);

    // ==================== 第一步：开票上下文 ====================

    const distributorLoading = ref(false);
    const distributorOptions = ref<Array<{ label: string; value: string }>>([]);
    /** 经销商编码 → 档案选项，提交时据此带出 distributorId */
    const distributorMap = reactive<Record<string, IndependentBillingDistributor>>({});

    const salesOrgLoading = ref(false);
    const salesOrgOptions = ref<IndependentBillingSalesOrg[]>([]);

    const invoiceInfoLoading = ref(false);
    const invoiceInfoOptions = ref<DeliInvoiceInfo[]>([]);
    // 开票信息下拉搜索关键字：按编码/发票抬头本地模糊过滤（数据量小，无需后端检索）
    const invoiceInfoSearchKey = ref('');
    const filteredInvoiceInfoOptions = computed(() => {
      const keyword = invoiceInfoSearchKey.value.trim().toLowerCase();
      if (!keyword) return invoiceInfoOptions.value;
      return invoiceInfoOptions.value.filter(
        (info) =>
          (info.code || '').toLowerCase().includes(keyword) ||
          (info.invoiceTitle || '').toLowerCase().includes(keyword)
      );
    });
    const handleInvoiceInfoSearch = (val: string) => {
      invoiceInfoSearchKey.value = val || '';
    };
    /** 选中/清除后同步清空搜索词（输入框由组件自动清空，列表过滤条件需手动复位） */
    const handleInvoiceInfoSelectChange = () => {
      invoiceInfoSearchKey.value = '';
    };
    /** 后端 queryDefaultInvoiceInfo 的返回：id 为空表示来自经销商档案（客户信息默认） */
    const defaultInvoiceInfo = ref<DeliInvoiceInfo | null>(null);
    const selectedInvoiceInfoId = ref<number | string | undefined>(undefined);

    const form = reactive({
      distributorCode: undefined as string | undefined,
      salesOrgCode: undefined as string | undefined,
      billingType: BillingType.VAT_SPECIAL as string,
      noticeInvoiceNo: '',
      deliveryAddress: '',
      remark: ''
    });

    // 开票类型可选项：与开票抽屉页一致，仅增值税专用发票/增值税普通发票（电子发票、红票选项已隐藏；
    // 红字场景不再由开票类型区分，改由勾选单据是否含负数驱动红字通知单号）
    const availableBillingTypes = computed(() => {
      const result: Record<string, string> = {};
      result[BillingType.VAT_SPECIAL] = billingTypeTextMap[BillingType.VAT_SPECIAL];
      result[BillingType.VAT_NORMAL] = billingTypeTextMap[BillingType.VAT_NORMAL];
      return result;
    });

    /** 是否存在经销商档案默认开票信息（后端返回的默认对象不带 id） */
    const hasArchiveDefault = computed(
      () => !!defaultInvoiceInfo.value && defaultInvoiceInfo.value.id == null
    );

    const isDefaultInvoiceInfo = computed(() => selectedInvoiceInfoId.value === DEFAULT_INVOICE_INFO);

    /** 是否存在任何可用开票信息：档案默认或已生效记录，二者皆无时禁止提交 */
    const hasAvailableInvoiceInfo = computed(
      () => hasArchiveDefault.value || invoiceInfoOptions.value.length > 0
    );

    const invoiceInfoDisabled = computed(() => !form.distributorCode || !form.salesOrgCode);

    const invoiceInfoPlaceholder = computed(() => {
      if (!form.distributorCode) return '请先选择经销商';
      if (!form.salesOrgCode) return '请先选择销售机构';
      return hasAvailableInvoiceInfo.value ? '请选择开票信息' : '无可用开票信息';
    });

    const invoiceInfoDisabledTip = computed(() => {
      if (!form.salesOrgCode) return '请先选择销售机构';
      if (!hasAvailableInvoiceInfo.value) return '当前经销商与销售机构未维护有效开票信息，请先完成维护';
      return '';
    });

    const currentInvoiceInfo = computed<Partial<DeliInvoiceInfo>>(() => {
      if (isDefaultInvoiceInfo.value) return defaultInvoiceInfo.value || {};
      const matched = invoiceInfoOptions.value.find((info) => info.id === selectedInvoiceInfoId.value);
      return matched || {};
    });

    /** 将前端枚举值（如 'ZF1'）转为后端 GraphQL 枚举名（如 'VAT_SPECIAL'） */
    const getBillingTypeEnumName = (val: string): string => {
      const entry = Object.entries(BillingType).find(([_, v]) => v === val);
      return entry ? entry[0] : val;
    };

    const loadDistributorOptions = async (keyword?: string) => {
      distributorLoading.value = true;
      try {
        const list = await queryDistributorOptions(keyword);
        list.forEach((item) => {
          distributorMap[item.distributorCode] = item;
        });
        distributorOptions.value = list.map((item) => ({
          label: `${item.distributorErpCode || '-'} / ${item.distributorName || '-'}（${item.distributorCode}）`,
          value: item.distributorCode
        }));
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        distributorLoading.value = false;
      }
    };

    let distributorSearchTimer: any = null;
    const handleDistributorSearch = (keyword: string) => {
      if (distributorSearchTimer) clearTimeout(distributorSearchTimer);
      distributorSearchTimer = setTimeout(() => loadDistributorOptions(keyword), DISTRIBUTOR_SEARCH_DEBOUNCE);
    };

    /**
     * 切换经销商或销售机构 → 清空已选开票信息与已勾选 80 单，重新计算默认值与下拉选项。
     * 候选单据归属经销商 + 销售机构，上下文一变原勾选即失效，必须一并清空。
     */
    const resetDependentState = () => {
      salesOrgOptions.value = [];
      invoiceInfoOptions.value = [];
      invoiceInfoSearchKey.value = '';
      defaultInvoiceInfo.value = null;
      selectedInvoiceInfoId.value = undefined;
      // 开票类型默认值随经销商档案联动：切换上下文后重置，待档案默认开票信息带回后回填
      form.billingType = BillingType.VAT_SPECIAL;
      selectedMap.value = {};
      candidateAllData.value = [];
      candidatePagination.total = 0;
      candidateTableRef.value?.clearCheckboxRow?.();
    };

    const loadSalesOrgOptions = async (distributorCode: string) => {
      salesOrgLoading.value = true;
      try {
        const list = await querySalesOrgOptions(distributorCode);
        salesOrgOptions.value = list || [];
        // 默认销售机构由后端排在首位，自动选中以减少一次点击
        const preferred = salesOrgOptions.value[0];
        if (preferred) {
          form.salesOrgCode = preferred.salesOrgCode;
          await loadInvoiceInfoOptions();
        }
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        salesOrgLoading.value = false;
      }
    };

    const loadInvoiceInfoOptions = async () => {
      if (!form.distributorCode || !form.salesOrgCode) return;
      invoiceInfoLoading.value = true;
      try {
        const [options, defaultInfo] = await Promise.all([
          queryInvoiceInfoOptions(form.distributorCode, form.salesOrgCode),
          queryDefaultInvoiceInfo(form.distributorCode, form.salesOrgCode)
        ]);
        invoiceInfoOptions.value = options || [];
        // 选项重载后同步复位搜索过滤条件
        invoiceInfoSearchKey.value = '';
        defaultInvoiceInfo.value = defaultInfo;
        // 默认带出：档案默认（无 id）优先，其次后端回退的默认/最新一条已生效记录
        if (defaultInfo && defaultInfo.id == null) {
          selectedInvoiceInfoId.value = DEFAULT_INVOICE_INFO;
          form.deliveryAddress = defaultInfo.deliveryAddress || '';
          // 开票类型取经销商档案默认（专票 -> ZF1，其余 -> ZF2），与开票抽屉页口径一致；
          // 档案未带出时保持默认专票
          form.billingType = defaultInfo.defaultBillingType || BillingType.VAT_SPECIAL;
        } else if (defaultInfo && defaultInfo.id != null) {
          selectedInvoiceInfoId.value = defaultInfo.id;
        } else {
          selectedInvoiceInfoId.value = undefined;
        }
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        invoiceInfoLoading.value = false;
      }
    };

    const handleDistributorChange = async (code?: string) => {
      form.salesOrgCode = undefined;
      resetDependentState();
      if (!code) return;
      await loadSalesOrgOptions(code);
    };

    const handleSalesOrgChange = async () => {
      resetDependentState();
      if (!form.salesOrgCode) return;
      await loadInvoiceInfoOptions();
    };

    // ==================== 第二步：候选单据 ====================

    const candidateTableRef = ref();
    const candidateLoading = ref(false);
    /**
     * 后端一次返回全量候选（≤候选池上限 2000，瞬态模型 GQL 只支持 List 返回形态），
     * 翻页改为本地切片，不再逐页请求
     */
    const candidateAllData = ref<IndependentBillingCandidate[]>([]);
    /** 当前页切片：vxe-table 的 checkbox 非受控，换页即换数据，勾选态由 selectedMap 回灌 */
    const candidateData = computed(() => {
      const { current, pageSize } = candidatePagination;
      const from = Math.min((current - 1) * pageSize, candidateAllData.value.length);
      const to = Math.min(from + pageSize, candidateAllData.value.length);
      return candidateAllData.value.slice(from, to);
    });
    /**
     * 勾选态以 90 单数据 ID 为键自维护：vxe-table 的 checkbox 是非受控的，
     * 翻页后当前页 DOM 重建会丢失勾选，故用本 map 作为跨页权威状态并在数据加载后回灌 UI。
     */
    const selectedMap = ref<Record<string, IndependentBillingCandidate>>({});
    const selectedCandidates = computed(() => Object.values(selectedMap.value));

    const candidateSearch = reactive({
      distributionChannelCode: '',
      tmsOrderCodes: [] as string[],
      order90Codes: [] as string[],
      lineStatus: undefined as string | undefined,
      dataPackageNos: [] as string[]
    });

    const candidatePagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0
    });

    const buildCandidateQuery = (): IndependentBillingCandidateFilter => ({
      distributorCode: form.distributorCode,
      salesOrgCode: form.salesOrgCode,
      distributionChannelCode: candidateSearch.distributionChannelCode.trim() || undefined,
      tmsOrderCodes: candidateSearch.tmsOrderCodes,
      order90Codes: candidateSearch.order90Codes,
      dataPackageNos: candidateSearch.dataPackageNos,
      lineStatus: candidateSearch.lineStatus as any
    });

    /** 数据加载后把自维护的勾选态回灌到 vxe-table，保证翻页回来仍显示已勾选 */
    const syncCheckboxUi = () => {
      const table = candidateTableRef.value;
      if (!table) return;
      table.clearCheckboxRow?.();
      const rows = candidateData.value.filter((row) => selectedMap.value[String(row.candidateId)]);
      if (rows.length > 0) {
        table.setCheckboxRow?.(rows, true);
      }
    };

    const fetchCandidates = async () => {
      if (!form.distributorCode || !form.salesOrgCode) {
        message.warning('请先选择经销商与销售机构');
        return;
      }
      candidateLoading.value = true;
      try {
        const list = await queryCandidateOrders(buildCandidateQuery());
        candidateAllData.value = list || [];
        candidatePagination.total = candidateAllData.value.length;
        await nextTick();
        syncCheckboxUi();
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        candidateLoading.value = false;
      }
    };

    const onCandidateCheckboxChange = () => {
      const records: IndependentBillingCandidate[] = candidateTableRef.value?.getCheckboxRecords?.() || [];
      const next = { ...selectedMap.value };
      // 先摘掉当前页全部行，再按 vxe 的勾选结果重新写入，保证取消勾选同样生效
      candidateData.value.forEach((row) => {
        delete next[String(row.candidateId)];
      });
      records.forEach((row) => {
        next[String(row.candidateId)] = row;
      });
      selectedMap.value = next;
    };

    const handleCandidateSearch = () => {
      candidatePagination.current = 1;
      fetchCandidates();
    };

    const handleCandidateReset = () => {
      candidateSearch.distributionChannelCode = '';
      candidateSearch.tmsOrderCodes = [];
      candidateSearch.order90Codes = [];
      candidateSearch.lineStatus = undefined;
      candidateSearch.dataPackageNos = [];
      candidatePagination.current = 1;
      fetchCandidates();
    };

    const handleCandidatePaginationChange = (page: number, pageSize: number) => {
      candidatePagination.current = page;
      candidatePagination.pageSize = pageSize;
      // 本地切片翻页，不重新请求；换页后回灌勾选态
      nextTick(() => syncCheckboxUi());
    };

    /** 全选当前查询结果：跨页勾选（不可选行已在数据源上被后端过滤，全量数据均为可选） */
    const handleSelectAllResult = () => {
      if (!form.distributorCode || !form.salesOrgCode) {
        message.warning('请先选择经销商与销售机构');
        return;
      }
      const list = candidateAllData.value;
      if (list.length === 0) {
        message.warning('当前查询条件下没有可选单据');
        return;
      }
      const next = { ...selectedMap.value };
      list.forEach((row) => {
        next[String(row.candidateId)] = row;
      });
      selectedMap.value = next;
      message.success(`已勾选当前查询结果中的 ${list.length} 条可选单据`);
      nextTick(() => syncCheckboxUi());
    };

    const handleClearSelection = () => {
      selectedMap.value = {};
      candidateTableRef.value?.clearCheckboxRow?.();
    };

    const selectedTaxInclusiveAmount = computed(() =>
      Math.round(
        selectedCandidates.value.reduce((sum, row) => sum + (Number(row.taxInclusiveAmount) || 0), 0) * 100
      ) / 100
    );

    /** 勾选单据中是否存在负数（红字场景）：价税合计为负。与后端 lineAmount(=taxInclusiveAmount) 判定口径一致 */
    const hasNegativeSelected = computed(() =>
      selectedCandidates.value.some((row) => (Number(row.taxInclusiveAmount) || 0) < 0)
    );

    // 非红字场景（勾选无负数单据）不允许填写红字通知单号，切换时清空已填值
    watch(hasNegativeSelected, (val) => {
      if (!val) form.noticeInvoiceNo = '';
    });

    const getOutStockStatusText = (val: string) => (val ? outStockStatusTextMap[val] || val : '-');

    // ==================== 提交 ====================

    const specifyVisible = ref(false);

    /**
     * 指定开票弹窗按开票行校验合计与行状态，向导阶段尚无开票行，
     * 故以候选行构造伪行（金额取 90 单净额/税额，状态置未打包）传入，使校验口径与开票抽屉页一致。
     */
    const specifySelectedLines = computed<BillingBillLine[]>(() =>
      selectedCandidates.value.map(
        (row) =>
          ({
            id: row.candidateId,
            billingNetAmount: row.billingNetAmount,
            billingTaxAmount: row.billingTaxAmount,
            status: BillingLineStatus.UNPACKED
          } as any)
      )
    );

    const validateStep1 = (): boolean => {
      if (!form.distributorCode) {
        message.warning('请选择经销商');
        return false;
      }
      if (!form.salesOrgCode) {
        message.warning('请选择销售机构');
        return false;
      }
      if (!hasAvailableInvoiceInfo.value) {
        message.error('当前经销商与销售机构未维护有效开票信息，请先完成维护');
        return false;
      }
      if (selectedInvoiceInfoId.value == null) {
        message.warning('请选择开票信息');
        return false;
      }
      if (!form.billingType) {
        message.warning('请选择开票类型');
        return false;
      }
      return true;
    };

    const handleNextStep = async () => {
      if (!validateStep1()) return;
      currentStep.value = 1;
      if (candidateData.value.length === 0) {
        await fetchCandidates();
      } else {
        await nextTick();
        syncCheckboxUi();
      }
    };

    const handlePrevStep = () => {
      currentStep.value = 0;
    };

    const handleCancel = () => {
      emit('cancel');
    };

    const buildPayload = (specifiedContent: SpecifiedInvoiceContentItem[] | null): IndependentBillingCreateParam => {
      const distributor = form.distributorCode ? distributorMap[form.distributorCode] : undefined;
      return {
        distributorId: distributor?.id,
        distributorCode: form.distributorCode as string,
        salesOrgCode: form.salesOrgCode as string,
        // 档案默认开票信息不传 id，由后端按经销商客户信息组装，口径与开票抽屉页 resetInvoiceInfo 一致
        invoiceInfoId: isDefaultInvoiceInfo.value ? undefined : (selectedInvoiceInfoId.value as number),
        billingType: getBillingTypeEnumName(form.billingType),
        // 仅红字场景（勾选含负数单据）传红字通知单号，其余场景不传
        noticeInvoiceNo: hasNegativeSelected.value ? form.noticeInvoiceNo.trim() || undefined : undefined,
        deliveryAddress: isDefaultInvoiceInfo.value ? form.deliveryAddress.trim() || undefined : undefined,
        remark: form.remark.trim() || undefined,
        candidateIds: selectedCandidates.value.map((row) => row.candidateId),
        specifiedInvoiceContent: specifiedContent ? JSON.stringify(specifiedContent) : null
      };
    };

    const doSubmit = async (specifiedContent: SpecifiedInvoiceContentItem[] | null) => {
      submitting.value = true;
      try {
        const bill: BillingBill | null = await createIndependentBilling(buildPayload(specifiedContent));
        if (bill) {
          message.success(`独立开票包 ${bill.code || ''} 已创建并发起打包`);
          emit('success', bill);
        }
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理（后端回显具体 80 单号与不可选原因），避免重复弹窗
      } finally {
        submitting.value = false;
      }
    };

    /** 普通开票：按勾选行创建独立开票包并发起打包 */
    const handlePack = () => {
      if (!validateStep1()) {
        currentStep.value = 0;
        return;
      }
      if (selectedCandidates.value.length === 0) {
        message.warning('请先勾选需要开票的单据');
        return;
      }
      if (hasNegativeSelected.value && !form.noticeInvoiceNo.trim()) {
        message.warning('勾选单据含负数，必须填写红字通知单号');
        return;
      }
      Modal.confirm({
        title: '发起打包开票',
        content: `确定为已勾选的 ${selectedCandidates.value.length} 条单据创建独立开票包并发起打包开票吗？`,
        onOk: () => doSubmit(null)
      });
    };

    /** 指定开票：先填指定开票内容，校验通过后随创建入参一并提交 */
    const handleSpecify = () => {
      if (!validateStep1()) {
        currentStep.value = 0;
        return;
      }
      if (selectedCandidates.value.length === 0) {
        message.warning('请先勾选需要开票的单据');
        return;
      }
      if (hasNegativeSelected.value && !form.noticeInvoiceNo.trim()) {
        message.warning('勾选单据含负数，必须填写红字通知单号');
        return;
      }
      specifyVisible.value = true;
    };

    const handleSpecifyDraftConfirm = (items: SpecifiedInvoiceContentItem[]) => {
      doSubmit(items);
    };

    onMounted(() => {
      loadDistributorOptions();
    });

    return {
      DEFAULT_INVOICE_INFO,
      BillingType,
      billingLineStatusTextMap,
      sapPackStatusTextMap,
      sapPackStatusColorMap,
      sourceDocTypeTextMap,
      currentStep,
      submitting,
      form,
      availableBillingTypes,
      distributorLoading,
      distributorOptions,
      salesOrgLoading,
      salesOrgOptions,
      invoiceInfoLoading,
      invoiceInfoOptions,
      filteredInvoiceInfoOptions,
      handleInvoiceInfoSearch,
      handleInvoiceInfoSelectChange,
      selectedInvoiceInfoId,
      hasArchiveDefault,
      isDefaultInvoiceInfo,
      hasAvailableInvoiceInfo,
      invoiceInfoDisabled,
      invoiceInfoPlaceholder,
      invoiceInfoDisabledTip,
      currentInvoiceInfo,
      handleDistributorSearch,
      handleDistributorChange,
      handleSalesOrgChange,
      candidateTableRef,
      candidateLoading,
      candidateData,
      candidateSearch,
      candidatePagination,
      selectedCandidates,
      selectedTaxInclusiveAmount,
      hasNegativeSelected,
      specifyVisible,
      specifySelectedLines,
      getLineStatusText,
      getLineStatusColor,
      getOutStockStatusText,
      formatAmount,
      getAmountClass,
      onCandidateCheckboxChange,
      fetchCandidates,
      handleCandidateSearch,
      handleCandidateReset,
      handleCandidatePaginationChange,
      handleSelectAllResult,
      handleClearSelection,
      handleNextStep,
      handlePrevStep,
      handleCancel,
      handlePack,
      handleSpecify,
      handleSpecifyDraftConfirm
    };
  }
});
